import React, { useState, useMemo } from 'react';
import type { extractReturnAsJson } from '@aidkitorg/types/lib/endpoints';
import type { Directory } from '@aidkitorg/aidkit/lib/directory';
import type { Translations } from '@aidkitorg/i18n/lib';
import { useLocalizedStrings } from "../Localization";
import { Loading, PaymentDisplay } from '@aidkitorg/component-library';
import { usePost } from '../API';
import { useAsyncEffect } from '../Hooks/AsyncEffect';
import { PaymentType } from "@aidkitorg/types/lib/payments/paymentTypes";

type Entry = extractReturnAsJson<Directory['/payments/rc_actions_v2']>['entries'][number];

function TopBanner(props: {
  applicantPaymentType: PaymentType,
  totalPaidInCents: number,
}) {
  const L = useLocalizedStrings();
  
  const totalPaidInDollars = (props.totalPaidInCents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD'
  });

  const paymentToReadableName = (paymentType: PaymentType) => {
    switch (paymentType) {
      case 'ach':
        return 'Dwolla ACH';
      case 'givecard_load':
      case 'givecard_mail':
        return L.questions.payment.physical_card.replace('$type', 'Givecard');
      case 'givecard_virtual':
        return L.questions.payment.virtual_card.replace('$type', 'Givecard');
      case 'givecard_rtp':
        return 'Givecard RTP';
      case 'usbank_card_mail':
      case 'usbank_card_load':
      case 'usbank_card_withdrawal':
        return L.questions.payment.physical_card.replace('$type', 'USBank');
      case 'usio_mail':
      case 'usio_pickup':
      case 'usio':
        return L.questions.payment.physical_card.replace('$type', 'USIO');
      case 'usio_virtual':
        return L.questions.payment.virtual_card.replace('$type', 'USIO');
      default:
        return paymentType;
    }
  }

  return (
    <div className="w-full ml-6 pr-2 py-4 pr-4 flex justify-between items-center border-b-4 border-indigo-600">
      <div className="flex flex-col px-2">
        <span className="text-sm text-gray-600">{L.questions.payment.total_paid}</span>
        <h3 className="pt-2">{totalPaidInDollars}</h3>
      </div>
      <div className="flex flex-col items-end">
        <span className="text-sm text-gray-600">{L.questions.payment.current_payment_method}</span>
        <h3 className="pt-2">{paymentToReadableName(props.applicantPaymentType)}</h3>
      </div>
    </div>
  );
}

export function ApplicantPaymentPage({ applicant }: { applicant: string }) {
  const L = useLocalizedStrings();
  const getPayments = usePost('/payments/rc_actions_v2');
  const [entries, setEntries] = useState<Entry[] | undefined>();

  const totalPaidInCents = useMemo(() => {
    let total = 0;
    entries?.forEach(({ action_state, amount, dwolla_float_ach_grant_extra }) => {
      const dwollaData = dwolla_float_ach_grant_extra?.dwolla_data;
      const isCompletedDwollaEntry = !!dwollaData
        && typeof dwollaData === 'object'
        && 'status' in dwollaData
        && dwollaData.status === 'processed';

      if (action_state === 'COMPLETE' || isCompletedDwollaEntry) {
        total += +amount;
      }
    });
    return total;
  }, [entries]);

  useAsyncEffect(async (signal) => {
    setEntries(undefined);

    const payments = await getPayments({ applicant }, signal);

    if (payments) {
      const { entries } = payments;

      const sortEntries = (a: Entry, b: Entry) => +new Date(b.proposed_date) - +new Date(a.proposed_date);

      setEntries(entries.sort(sortEntries));
    }
  }, [applicant]);

  if (!entries) {
    return <Loading loading />;
  }

  if (!entries.length) {
    return <p className='text-center mt-5'>{L.questions.payment.no_payments}</p>;
  }

  return <div className='flex flex-col py-6'>
    <TopBanner
      applicantPaymentType={'ach'}
      totalPaidInCents={totalPaidInCents}
    />
    <div className='flex flex-col gap-4 p-6'>
      {entries.map((entry) =>
        <ApplicantPayment key={entry.id} entry={entry} />
      )}
    </div>
  </div>;
}

function getPaymentTypeForActionKind(actionKind: string | null) {
  switch (actionKind) {
    case 'dwolla_float_ach_grant':
      return 'ach';

    case 'givecard_load':
    case 'givecard_mail':
    case 'givecard_rtp':
    case 'givecard_virtual':
      return actionKind;

    case 'usbank_card_load':
      return 'usbank_load';
    case 'usbank_card_mail':
      return 'usbank_mail';

    case 'load_usio_card':
    case 'usio_load_v2':
      return 'usio_load';
    case 'usio_mailed_grant':
      return 'usio_mail';

    default:
      return null;
  }
}

function getPaymentName(targetField: string | undefined, attempt: string | undefined, L: Translations) {
  if (!targetField) {
    return L.applicant.payment;
  }

  const prettyTargetField = targetField
    .split('_')
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(' ');

  // Attempt is zero-indexed so the second attempt is attempt 1
  // but we want to show that as attempt 2
  const attemptNumber = attempt ? +attempt + 1 : null;

  if (attemptNumber && !isNaN(attemptNumber)) {
    const attemptText = L.questions.payment.attempt.replace('$n', attemptNumber.toString());
    return `${prettyTargetField}${attemptText}`;
  } else {
    return prettyTargetField;
  }
}

function ApplicantPayment({ entry }: { entry: Entry }) {
  const L = useLocalizedStrings();

  const paymentType = getPaymentTypeForActionKind(entry.action_kind);

  if (!paymentType || entry.action_state === 'ANTICIPATED') {
    return null;
  }

  return <PaymentDisplay
    paymentConfig={{
      id: entry.id,
      paymentType,
      proposedAt: new Date(entry.proposed_date),
      approvedAt: entry.approval_date ? new Date(entry.approval_date) : undefined,
      completedAt: entry.issued_date ? new Date(entry.issued_date) : undefined,
      failedAt: entry.failure_date ? new Date(entry.failure_date) : undefined,
      status: entry.action_state,
      paymentName: getPaymentName(entry.action_target_field, entry.action_attempt_number, L),
      failure: entry.failure_reason ? { description: entry.failure_reason } : undefined,
      amountInCents: +entry.amount,
    }}
  />;
}
